import { useMemo, useState } from 'react';
import { Layout, Menu, Breadcrumb, Dropdown, Button } from 'antd';
import type { MenuProps } from 'antd';
import {
  UserOutlined,
  ReadOutlined,
  FileTextOutlined,
  TeamOutlined,
  LogoutOutlined,
  KeyOutlined,
  ExperimentOutlined,
  GiftOutlined,
  BugOutlined,
  DatabaseOutlined,
  AlignLeftOutlined,
  CreditCardOutlined,
  TagOutlined,
  MessageOutlined,
  BarChartOutlined,
  AppstoreOutlined,
  SearchOutlined,
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  ReloadOutlined,
  FullscreenOutlined,
  DownOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/auth';

const { Header, Sider, Content } = Layout;

export default function MainLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const [collapsed, setCollapsed] = useState(false);

  const menuItems = useMemo<NonNullable<MenuProps['items']>>(() => {
    const items: NonNullable<MenuProps['items']> = [
      { key: '/dashboard', icon: <BarChartOutlined />, label: '统计' },
      { key: '/profile', icon: <UserOutlined />, label: '个人中心' },
      { key: '/tutorial', icon: <ReadOutlined />, label: '使用教程' },
      {
        key: 'apps-group',
        icon: <AppstoreOutlined />,
        label: '程序管理',
        children: [
          { key: '/apps', icon: <AppstoreOutlined />, label: '软件列表' },
          { key: '/apps/1/api', icon: <AlignLeftOutlined />, label: 'WebApi接口' },
          { key: '/apps/1/variables', icon: <DatabaseOutlined />, label: '变量数据' },
          { key: '/apps/1/algorithms', icon: <AlignLeftOutlined />, label: '远程算法' },
          { key: '/apps/1/blacklist', icon: <BugOutlined />, label: '黑名单列表' },
        ],
      },
      { key: '/error-codes', icon: <BugOutlined />, label: '错误代码对照表' },
      {
        key: 'users-group',
        icon: <TeamOutlined />,
        label: '用户管理',
        children: [
          { key: '/admin/users', icon: <TeamOutlined />, label: '用户列表' },
          { key: '/admin/users/cards', icon: <CreditCardOutlined />, label: '用户充值卡' },
        ],
      },
      {
        key: 'keys-group',
        icon: <KeyOutlined />,
        label: '单码管理',
        children: [
          { key: '/apps/1/keys', icon: <KeyOutlined />, label: '单码列表' },
        ],
      },
      {
        key: 'query-group',
        icon: <SearchOutlined />,
        label: '激活/制卡查询',
        children: [
          { key: '/apps/1/keys/query', icon: <SearchOutlined />, label: '查询' },
        ],
      },
      {
        key: 'trial-group',
        icon: <ExperimentOutlined />,
        label: '试用管理',
        children: [
          { key: '/apps/1/trial', icon: <ExperimentOutlined />, label: '试用列表' },
        ],
      },
      {
        key: 'agents-group',
        icon: <GiftOutlined />,
        label: '代理管理',
        children: [
          { key: '/admin/agents', icon: <GiftOutlined />, label: '代理列表' },
          { key: '/admin/agents/cards', icon: <CreditCardOutlined />, label: '代理充值卡' },
          { key: '/admin/agents/types', icon: <TagOutlined />, label: '代理售卡类型' },
          { key: '/admin/agents/announcement', icon: <MessageOutlined />, label: '设置代理端公告' },
        ],
      },
      { key: '/logs', icon: <FileTextOutlined />, label: '日志查询' },
    ];
    return items;
  }, []);

  const breadcrumbItems = useMemo(() => {
    const path = location.pathname;
    const items: { title: string }[] = [{ title: '首页' }];

    if (path.startsWith('/apps')) {
      items.push({ title: '程序管理' });
      if (path === '/apps') items.push({ title: '软件列表' });
      else if (path.includes('/api')) items.push({ title: 'WebApi接口' });
      else if (path.includes('/variables')) items.push({ title: '变量数据' });
      else if (path.includes('/algorithms')) items.push({ title: '远程算法' });
      else if (path.includes('/blacklist')) items.push({ title: '黑名单列表' });
      else if (path.includes('/keys/query')) items.push({ title: '激活/制卡查询' });
      else if (path.includes('/keys')) items.push({ title: '单码管理' });
      else if (path.includes('/trial')) items.push({ title: '试用管理' });
    } else if (path.startsWith('/admin/users')) {
      items.push({ title: '用户管理' });
      if (path.includes('/cards')) items.push({ title: '用户充值卡' });
      else items.push({ title: '用户列表' });
    } else if (path.startsWith('/admin/agents')) {
      items.push({ title: '代理管理' });
      if (path.includes('/cards')) items.push({ title: '代理充值卡' });
      else if (path.includes('/types')) items.push({ title: '代理售卡类型' });
      else if (path.includes('/announcement')) items.push({ title: '设置代理端公告' });
      else items.push({ title: '代理列表' });
    } else if (path === '/error-codes') {
      items.push({ title: '错误代码对照表' });
    } else if (path === '/logs') {
      items.push({ title: '日志查询' });
    } else if (path === '/dashboard') {
      items.push({ title: '统计' });
    } else if (path === '/profile') {
      items.push({ title: '个人中心' });
    } else if (path === '/tutorial') {
      items.push({ title: '使用教程' });
    }

    return items;
  }, [location.pathname]);

  const selectedKey = useMemo(() => {
    return location.pathname;
  }, [location.pathname]);

  const openKeys = useMemo(() => {
    const path = location.pathname;
    const keys: string[] = [];
    if (path.startsWith('/apps')) keys.push('apps-group');
    if (path.startsWith('/admin/users')) keys.push('users-group');
    if (path.includes('/keys') && !path.includes('/query')) keys.push('keys-group');
    if (path.includes('/query')) keys.push('query-group');
    if (path.includes('/trial')) keys.push('trial-group');
    if (path.startsWith('/admin/agents')) keys.push('agents-group');
    return keys;
  }, [location.pathname]);

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    navigate(key);
  };

  const handleUserMenuClick: MenuProps['onClick'] = async ({ key }) => {
    if (key === 'logout') {
      await logout();
      navigate('/login', { replace: true });
    } else {
      navigate(key);
    }
  };

  const userMenuItems: MenuProps['items'] = [
    { key: '/profile', icon: <UserOutlined />, label: '个人中心' },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', danger: true },
  ];

  return (
    <Layout style={{ minHeight: '100vh', background: '#f2f2f2' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        theme="dark"
        width={200}
        style={{
          background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 100,
        }}
      >
        <div
          style={{
            height: 56,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            color: '#fff',
            fontWeight: 600,
            fontSize: collapsed ? 14 : 18,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          {collapsed ? (
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: 14,
              }}
            >
              W
            </div>
          ) : (
            <>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: 16,
                }}
              >
                W
              </div>
              <span>文心云</span>
            </>
          )}
        </div>
        <Menu
          mode="inline"
          theme="dark"
          selectedKeys={[selectedKey]}
          defaultOpenKeys={openKeys}
          items={menuItems}
          onClick={handleMenuClick}
          style={{
            borderRight: 0,
            background: 'transparent',
            marginTop: 8,
          }}
        />
      </Sider>

      <Layout style={{ marginLeft: collapsed ? 80 : 200, background: '#f2f2f2', transition: 'all 0.2s' }}>
        <Header
          style={{
            background: '#fff',
            padding: '0 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: 48,
            lineHeight: '48px',
            boxShadow: 'none',
            borderBottom: '1px solid #e8e8e8',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{ fontSize: 16 }}
            />
            <Breadcrumb items={breadcrumbItems} style={{ fontSize: 13 }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Button type="text" icon={<ReloadOutlined />} size="small" />
            <Button type="text" icon={<FullscreenOutlined />} size="small" />
            <Dropdown
              menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
              placement="bottomRight"
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  cursor: 'pointer',
                  padding: '0 4px',
                  fontSize: 13,
                }}
              >
                <span>{user?.username ?? '未登录'}</span>
                <DownOutlined style={{ fontSize: 10 }} />
              </div>
            </Dropdown>
            <Button type="text" icon={<MoreOutlined />} size="small" />
          </div>
        </Header>

        <Content
          style={{
            margin: 16,
            padding: 16,
            background: '#fff',
            borderRadius: 2,
            minHeight: 'calc(100vh - 80px)',
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
